import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/clerk';
import { useApp } from '../../context/AppContext';
import { readBookPage, rememberBookPage, rememberSignedInUser } from '../../utils/lastProject';

// Pages that are not inside a book. Coming back to the site never lands here.
const NOT_BOOK_PAGES = [/^\/$/, /^\/projects\/?$/, /^\/admin/, /^\/onboarding/, /^\/sign-(in|up)/];

export function isBookPage(pathname) {
  const path = pathname || '';
  return path.startsWith('/') && !NOT_BOOK_PAGES.some((rule) => rule.test(path));
}

// The address the app was opened at. Only that first visit to "/" resumes;
// later in-app moves to "/" (after a delete, say) still go to Projects.
const OPENED_AT = typeof window === 'undefined' ? '' : window.location.pathname;
let resumeSpent = false;

export function resumePathOnOpen(openedAt = OPENED_AT) {
  if (resumeSpent) return null;
  resumeSpent = true;
  if (openedAt !== '/') return null;
  const path = readBookPage();
  return path && isBookPage(path.split(/[?#]/)[0]) ? path : null;
}

export function resetResumeForTests() {
  resumeSpent = false;
}

/**
 * Runs during render, before the routes, so a new account cannot resume
 * another account's book. A later effect would be too late.
 */
export function ClaimBookMemory() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  if (isLoaded && isSignedIn && userId) rememberSignedInUser(userId);
  return null;
}

/** "/" route: back to the page the writer left in their book, else Projects. */
export function ResumeOrProjects() {
  const path = resumePathOnOpen();
  return <Navigate to={path || '/projects'} replace />;
}

/** /novels, /home, and any other unknown path land on the projects list. */
export function UnknownPathRedirect() {
  return <Navigate to="/projects" replace />;
}

/** Keeps the current page as the book's return point while a book is open. */
export function RememberBookPage() {
  const location = useLocation();
  const { currentStory } = useApp();
  const storyId = currentStory?.id;
  useEffect(() => {
    if (!storyId || !isBookPage(location.pathname)) return;
    rememberBookPage(`${location.pathname}${location.search}`);
  }, [storyId, location.pathname, location.search]);
  return null;
}
